import angular from "angular";

import { AccountingData, Calculation, CalculationListItem, Dates, PeriodDateKind, PeriodType, Reports } from "@salaxy/core";
import { UiHelpers } from "../../services";

/**
 * Provides functionality for building ad hoc accounting reports.
 */
export class AccountingReportQueryController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "Reports",
    "UiHelpers",
  ];

  /**
   * Report view table only (partial) or pdf preview (full)
   * Defaults to 'partial'
   */
  public view: "partial" | "full";
  /** Query type: Query period or set of id's from database or calcs to specify the calculations. */
  public queryType: "periodQuery" | "selectionQuery" | "calcs" = "periodQuery";

  /** Period type for the query. */
  public periodType: PeriodType = PeriodType.Month;

  /** Period date kind for the query. */
  public periodDateKind: PeriodDateKind = PeriodDateKind.PaidAtDate;

  /** Ref date for the period. */
  public refDate?: string;

  /** End date for the custom period. */
  public endDate?: string;

  /** Accounting data */
  public data: AccountingData;

  /**Selected report language */
  public lang: string;

  /**
   * Calculations that are selected by used to show be fetched and shown in reports.
   * Only the ID is really used: the rest of the calculation data is fetched from database.
   */
  public calculations: CalculationListItem[] = [];

  /**
   * Calculations that are shown in the database directly.
   * If set, will hide the query interface and set queryType accordingly (only in init).
   */
  public calcs: Calculation[];

  /** Data reader for the control. */
  public dataReader = {
  /**
   * Function to read data into given array
   * TODO: Should make interface for this or otherwise go-through.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
   read: (target: string, ruleSet: string, arr: AccountingData[], message: string): Promise<void> => {
      if (this.data) {
        arr.push(this.data);
      }
      return Promise.resolve();
    },
    /** Optional label for data export */
    exportLabel: null,
    /** Indicates if the export is not possible */
    disabled: () => !this.data,
    /* Indicates if row is expandable */
    showExpand: true,
    /* Indicates if rows exists*/
    hasRows: () => !this.data?.ledgerAccounts
  };

  constructor(
    private reportsApi: Reports,
    private uiHelpers: UiHelpers,
  ) {
    // Dependency injection
  }

  /**
   * Initialize controller values.
   */
  public $onInit() {
    this.view = this.view || "partial";
    this.refDate = Dates.format(Dates.add("today",-1, "month"),"YYYY-MM-01");
    if (this.calcs?.length > 0) {
      this.queryType = "calcs";
      this.queryData();
    }
  }

  /**
   * Opens the list of calculations into a dialog window for selection and then adds them to the selected calculations
   * @param category Either "paid" or "draft".
   */
  public addCalcs(category: "paid" | "draft" = "draft") {
    const selectedCalculations: CalculationListItem[] = [];
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/calc/CalcList.html",
      selectedCalculations,
      {
        title: (category === "paid" ? "Valitse maksetut palkat" : "Valitse luonnokset"),
        category,
      }).then((result) => {
        if (result.action === "ok" && result.item.length > 0) {
          for (const selectedCalculation of result.item) {
            if (!this.calculations.some((x) => x.id === selectedCalculation.id)) {
              this.calculations.push(selectedCalculation);
            }
          }
        }
      });
  }

  /** Removes the selected calculation from the calculations list. */
  public removeCalc(calculation: any) {
    this.calculations = this.calculations.filter((x) => x.id !== calculation.id);
  }

  /** Queries the report data. */
  public queryData(target = "default") {
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    switch (this.queryType) {
      case "periodQuery":
        this.reportsApi.getAccountingDataForPeriod(this.refDate, target, this.periodType, this.endDate, this.periodDateKind)
          .then((result) => {
            this.data = result;
            loading.dismiss();
          });
        break;
      case "selectionQuery": {
        const calcIds = this.calculations.map((x) => x.id);
        this.reportsApi.getAccountingDataForCalculationIds(calcIds, target)
          .then((result) => {
            this.data = result;
            loading.dismiss();
          });
        break;
      }
      case "calcs":
        this.reportsApi.getAccountingDataForCalculations(this.calcs, target)
          .then((result) => {
            this.data = result;
            loading.dismiss();
          });
        break;
      default:
        loading.dismiss();
        throw new Error("Unknown queryType: " + this.queryType);
    }
  }
}
